import { Link } from "react-router-dom";

import {
  APP_ENTRY_ROUTE,
  BENTO_CARDS,
  BENTO_METRICS,
  FEE_TIERS,
  POOLS,
  STEPS,
} from "../landingContent";

const TH =
  "px-20 py-13 text-[10.5px] font-bold uppercase border-b";
const TD = "px-20 py-15 border-b";

/** Execution thesis — one wide card plus four standard cards. */
export function ThesisSection() {
  return (
    <section className="l-soft border-b" style={{ borderColor: "var(--line)" }}>
      <div className="l-container py-88">
        <div data-reveal className="mb-44" style={{ maxWidth: "70ch" }}>
          <p className="l-eyebrow">Execution Thesis</p>
          <h2 className="l-h2">Three frictions kill alpha. We engineered out all three.</h2>
          <p className="l-intro">
            Spread, slippage and latency compound invisibly across every trade. USDX addresses each one — and
            publishes the measurement.
          </p>
        </div>

        <div className="l-bento gap-16">
          <div data-reveal className="l-bento-wide l-card l-card-hover relative overflow-hidden p-28">
            <div
              className="pointer-events-none absolute h-[260px] w-[260px]"
              style={{
                right: -40,
                top: -40,
                background: "radial-gradient(circle, var(--glow-a), transparent 70%)",
              }}
            />
            <span className="l-eyebrow relative" style={{ letterSpacing: "0.12em", margin: 0 }}>
              01 — High liquidity
            </span>
            <h3
              className="relative mt-12 text-26 font-bold"
              style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}
            >
              Kill the spread tax
            </h3>
            <p
              className="relative mb-22 mt-10 text-15"
              style={{ color: "var(--ink-muted)", maxWidth: "52ch", textWrap: "pretty" }}
            >
              Multi-venue aggregation with a dedicated market-maker programme. Depth protocols that hold when others
              fracture — $37.1M resting across the top five levels.
            </p>
            <div
              className="l-hairline relative rounded-12"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}
            >
              {BENTO_METRICS.map((metric) => (
                <div key={metric.label} className="flex flex-col gap-3 px-16 py-14">
                  <span className="l-micro" style={{ letterSpacing: "0.07em", fontSize: 10 }}>
                    {metric.label}
                  </span>
                  <span
                    className="l-nums text-[18px] font-bold"
                    style={{ letterSpacing: "-0.02em", color: "var(--brand600)" }}
                  >
                    {metric.value}
                  </span>
                  <span className="text-[10.5px]" style={{ color: "var(--ink-subtle)" }}>
                    {metric.note}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {BENTO_CARDS.map((card) => (
            <div key={card.eyebrow} data-reveal className="l-card l-card-hover flex flex-col gap-10 p-26">
              <span className="l-eyebrow" style={{ letterSpacing: "0.12em", margin: 0 }}>
                {card.eyebrow}
              </span>
              <h3
                className="mt-2 text-[21px] font-bold"
                style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.018em" }}
              >
                {card.title}
              </h3>
              <p className="flex-1 text-[14.5px]" style={{ color: "var(--ink-muted)", textWrap: "pretty" }}>
                {card.body}
              </p>
              <div
                className="mt-4 flex items-baseline gap-8 border-t pt-14"
                style={{ borderColor: "var(--line-soft)" }}
              >
                <span className="l-nums text-24 font-bold" style={{ letterSpacing: "-0.025em" }}>
                  {card.stat}
                </span>
                <span className="text-12" style={{ color: "var(--ink-subtle)" }}>
                  {card.statNote}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StepsSection() {
  return (
    <section id="how" className="border-b" style={{ borderColor: "var(--line)" }}>
      <div className="l-container py-88">
        <div data-reveal className="mb-52 text-center">
          <p className="l-eyebrow">How it works</p>
          <h2 className="l-h2">Three steps to your first fill</h2>
        </div>

        <div className="l-steps gap-20">
          {STEPS.map((step) => (
            <div key={step.n} data-reveal className="l-card l-card-hover flex flex-col gap-12 px-26 py-30">
              <span
                className="grid h-42 w-42 place-items-center rounded-12 text-16 font-extrabold"
                style={{
                  background: "var(--sunken)",
                  color: "var(--brand600)",
                  fontFamily: "var(--font-display)",
                }}
              >
                {step.n}
              </span>
              <h3
                className="mt-4 text-20 font-bold"
                style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.018em" }}
              >
                {step.title}
              </h3>
              <p className="text-[14.5px]" style={{ color: "var(--ink-muted)", textWrap: "pretty" }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeesSection() {
  return (
    <section id="fees" className="l-soft border-b" style={{ borderColor: "var(--line)" }}>
      <div className="l-container py-88">
        <div data-reveal className="mb-40" style={{ maxWidth: "64ch" }}>
          <p className="l-eyebrow">Fee structure</p>
          <h2 className="l-h2">Priced per volume tier. No hidden spread.</h2>
          <p className="l-intro">
            30-day rolling volume determines your tier. Maker rebates are paid in USDC at every epoch close.
          </p>
        </div>

        <div
          data-reveal
          className="overflow-hidden rounded-16 border"
          style={{ borderColor: "var(--line)", background: "var(--surface)" }}
        >
          <div className="overflow-x-auto">
            <table
              className="w-full text-14"
              style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 640 }}
            >
              <thead>
                <tr>
                  {["Tier", "30d Volume", "Maker", "Taker", "Max Leverage"].map((label, i) => (
                    <th
                      key={label}
                      className={TH}
                      style={{
                        textAlign: i < 2 ? "left" : "right",
                        letterSpacing: "0.09em",
                        color: "var(--ink-subtle)",
                        borderColor: "var(--line)",
                        background: "var(--surface-muted)",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEE_TIERS.map((tier) => (
                  <tr key={tier.tier}>
                    <td className={TD} style={{ borderColor: "var(--line-soft)", fontWeight: 700 }}>
                      {tier.tier}
                    </td>
                    <td
                      className={`${TD} l-nums`}
                      style={{ borderColor: "var(--line-soft)", color: "var(--ink-muted)" }}
                    >
                      {tier.volume}
                    </td>
                    <td
                      className={`${TD} l-nums text-right font-semibold`}
                      style={{ borderColor: "var(--line-soft)", color: "var(--success)" }}
                    >
                      {tier.maker}
                    </td>
                    <td
                      className={`${TD} l-nums text-right font-semibold`}
                      style={{ borderColor: "var(--line-soft)", color: "var(--ink-soft)" }}
                    >
                      {tier.taker}
                    </td>
                    <td
                      className={`${TD} l-nums text-right`}
                      style={{ borderColor: "var(--line-soft)", color: "var(--ink-muted)" }}
                    >
                      {tier.leverage}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export function EarnSection() {
  return (
    <section id="earn" className="border-b" style={{ borderColor: "var(--line)" }}>
      <div className="l-container py-88">
        <div data-reveal className="mb-40 flex flex-wrap items-end justify-between gap-24">
          <div style={{ maxWidth: "56ch" }}>
            <p className="l-eyebrow">Earn</p>
            <h2 className="l-h2">$340M in staked liquidity</h2>
            <p className="l-intro">
              Provide depth to the venues that back every fill. Yield is real protocol revenue, distributed per epoch.
            </p>
          </div>
          <Link to={APP_ENTRY_ROUTE} className="l-pill l-pill-primary h-44 px-24 text-[14.5px]">
            Start earning
          </Link>
        </div>

        <div className="l-pools gap-16">
          {POOLS.map((pool) => (
            <div
              key={pool.short}
              data-reveal
              className="l-card l-card-hover relative flex flex-col gap-14 overflow-hidden p-24"
            >
              <div
                className="pointer-events-none absolute h-[200px] w-[200px] rounded-full"
                style={{
                  right: -60,
                  bottom: -60,
                  background: "radial-gradient(circle, var(--glow-a), transparent 70%)",
                }}
              />
              <div className="relative flex items-center gap-11">
                <span
                  className="grid h-38 w-38 place-items-center rounded-11 text-14 font-extrabold text-white"
                  style={{ background: pool.mark, fontFamily: "var(--font-display)" }}
                >
                  {pool.short}
                </span>
                <div className="flex flex-col" style={{ lineHeight: 1.3 }}>
                  <span
                    className="text-16 font-bold"
                    style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.015em" }}
                  >
                    {pool.name}
                  </span>
                  <span className="text-[12.5px]" style={{ color: "var(--ink-subtle)" }}>
                    {pool.desc}
                  </span>
                </div>
              </div>

              <div className="relative mt-6 flex items-baseline gap-8">
                <span
                  className="l-nums text-[40px] font-extrabold"
                  style={{
                    fontFamily: "var(--font-display)",
                    letterSpacing: "-0.032em",
                    lineHeight: 1,
                    color: "var(--brand600)",
                  }}
                >
                  {pool.apr}
                </span>
                <span
                  className="text-12 font-bold"
                  style={{ letterSpacing: "0.08em", color: "var(--ink-subtle)" }}
                >
                  APR
                </span>
              </div>

              <div className="l-hairline relative mt-4 rounded-10" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="flex flex-col gap-2 px-12 py-10">
                  <span className="l-micro" style={{ fontSize: 9.5, letterSpacing: "0.08em" }}>
                    TVL
                  </span>
                  <span className="l-nums text-14 font-semibold">{pool.tvl}</span>
                </div>
                <div className="flex flex-col gap-2 px-12 py-10">
                  <span className="l-micro" style={{ fontSize: 9.5, letterSpacing: "0.08em" }}>
                    Epoch
                  </span>
                  <span className="l-nums text-14 font-semibold">{pool.epoch}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
